import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import { useLocation } from "react-router";
import type { RootState, AppDispatch } from "@/features/redux/store";
import {
  dequeueAndSetCurrent,
  completeCurrentTask,
  skipCurrentTask,
  deferTasksToGating,
  restoreDeferredGating,
  drainFinished,
  requeueCurrentTask,
  type TaskDef,
} from "./modalOrchestratorSlice";
import { isSessionOnceUsed, markSessionOnceUsed } from "./sessionOnce";
import {
  taskExecutors,
  passesPopupGating,
  type ExecutorContext,
} from "./taskExecutors";
import {
  useGlobalMobileGameIframeOpen,
  useGlobalOpenWallet,
  useGlobalOpenSearch,
  useGlobalOpenUserCenter,
  useMobileLeftSiderSwitch,
  useGlobalOpenRedPack,
} from "@/store/commonStore";
import {
  isDialogPlusVisible,
  DIALOG_VISIBILITY_CHANGED,
} from "@/componentsUI/Dialog/dialogVisibility";

/**
 * 判断当前路由是否为首页。
 */
const isHomePage = (pathname: string): boolean =>
  pathname === "/" || pathname.includes("/home");

/** 一个 store 一个队列；异步执行期间 reset 会立即取消旧呈现及等待。 */
export const useOrchestratorDrain = () => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const location = useLocation();
  const [mobileGameIframeOpen] = useGlobalMobileGameIframeOpen();
  const [walletOpen] = useGlobalOpenWallet();
  const [searchOpen] = useGlobalOpenSearch();
  const [userCenterOpen] = useGlobalOpenUserCenter();
  const [leftSiderOpen] = useMobileLeftSiderSwitch();
  const [redPackOpen] = useGlobalOpenRedPack();
  const stateRef = useRef({
    mobileGameIframeOpen,
    walletOpen,
    searchOpen,
    userCenterOpen,
    leftSiderOpen,
    redPackOpen,
    pathname: location.pathname,
  });
  stateRef.current = {
    mobileGameIframeOpen,
    walletOpen,
    searchOpen,
    userCenterOpen,
    leftSiderOpen,
    redPackOpen,
    pathname: location.pathname,
  };

  const buildCtx = useCallback(
    (): ExecutorContext => ({
      isMobileGameIframeOpen: !!stateRef.current.mobileGameIframeOpen,
      isWalletOpen: !!stateRef.current.walletOpen,
      isOtherPanelOpen: !!(
        stateRef.current.searchOpen ||
        stateRef.current.userCenterOpen ||
        stateRef.current.leftSiderOpen ||
        stateRef.current.redPackOpen
      ),
      isPageVisible: document.visibilityState !== "hidden",
      isDialogPlusOpen: isDialogPlusVisible(),
      isOnHomePage: isHomePage(stateRef.current.pathname),
    }),
    []
  );

  useEffect(() => {
    let disposed = false;
    let running = false;
    let scheduled = false;
    let activeTask: TaskDef | null = null;
    let controller: AbortController | undefined;
    const matchesCurrent = () =>
      !!activeTask &&
      store.getState().modalOrchestrator.currentTask === activeTask;

    const run = async () => {
      if (disposed || running) return;
      running = true;
      try {
        while (!disposed) {
          const { queue } = store.getState().modalOrchestrator;
          if (!queue.length || document.visibilityState === "hidden") break;
          if (queue[0].priority <= 40 && !passesPopupGating(buildCtx())) {
            dispatch(deferTasksToGating());
            continue;
          }
          dispatch(dequeueAndSetCurrent());
          const task = store.getState().modalOrchestrator.currentTask;
          if (!task) break;
          activeTask = task;
          controller = new AbortController();
          if (
            task.sessionOnceKey &&
            isSessionOnceUsed(task.sessionOnceKey, store)
          ) {
            dispatch(skipCurrentTask("session_once"));
            activeTask = null;
            continue;
          }
          const executor = taskExecutors[task.type];
          if (!executor) {
            dispatch(skipCurrentTask("no_executor"));
            activeTask = null;
            continue;
          }
          try {
            await executor(task.payload, buildCtx(), controller.signal);
            if (!disposed && !controller.signal.aborted && matchesCurrent()) {
              if (task.sessionOnceKey)
                markSessionOnceUsed(task.sessionOnceKey, store);
              // 先释放执行权，避免自身 complete 引发订阅取消。
              activeTask = null;
              dispatch(completeCurrentTask());
            }
          } catch (error) {
            if (!disposed && !controller.signal.aborted && matchesCurrent()) {
              activeTask = null;
              dispatch(skipCurrentTask("error"));
              console.warn("[orchestrator] task failed", task.id, error);
            }
          } finally {
            activeTask = null;
            controller = undefined;
          }
        }
      } finally {
        running = false;
        const state = store.getState().modalOrchestrator;
        if (
          !disposed &&
          !state.queue.length &&
          !state.currentTask &&
          state.status !== "idle"
        ) {
          dispatch(drainFinished());
        }
      }
    };

    // 同步观察 reset，及时取消 NiceModal，避免旧 show Promise 永久悬挂。
    const schedule = () => {
      if (activeTask && !matchesCurrent()) controller?.abort();
      if (disposed || scheduled) return;
      scheduled = true;
      void Promise.resolve().then(() => {
        scheduled = false;
        return run();
      });
    };
    const wake = () => {
      if (passesPopupGating(buildCtx())) dispatch(restoreDeferredGating());
      schedule();
    };
    const unsubscribe = store.subscribe(schedule);
    document.addEventListener("visibilitychange", wake);
    window.addEventListener(DIALOG_VISIBILITY_CHANGED, wake);
    schedule();
    return () => {
      disposed = true;
      unsubscribe();
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener(DIALOG_VISIBILITY_CHANGED, wake);
      controller?.abort();
      if (matchesCurrent()) dispatch(requeueCurrentTask());
    };
  }, [buildCtx, dispatch, store]);

  useEffect(() => {
    if (passesPopupGating(buildCtx())) dispatch(restoreDeferredGating());
  }, [
    buildCtx,
    dispatch,
    location.pathname,
    mobileGameIframeOpen,
    walletOpen,
    searchOpen,
    userCenterOpen,
    leftSiderOpen,
    redPackOpen,
  ]);
};
